import { styled } from '@mui/material';
import { useMemo, useState } from 'react';

import { useI18n } from '@app-velocity/i18n';
import { PersonaSelector } from '@app-velocity/modules/personas';
import {
  useStartAiExtendedReviewMutation,
  useStartAiReviewMutation,
} from '@app-velocity/modules/scenarios';
import { useCurrentUser } from '@app-velocity/libs/auth';
import {
  Button,
  ButtonsContainer,
  Modal,
  Typography,
  useModalContext,
  useModalWrapperContext,
} from '@app-velocity/ui';

import { usePrototypePermissions } from '../../../../hooks/usePrototypePermissions';

const StyledPersonaSelector = styled(PersonaSelector)({
  width: '100%',
});

const ModalContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));

const isRunFigmaConfidenceSimulationModalData = (
  data: unknown
): data is {
  teamId: string;
  scenarioId: string;
  amount: number;
  shareToken: string;
} => {
  const castedData = data as {
    teamId?: string;
    scenarioId?: string;
    amount?: number;
    shareToken?: string;
  };

  return !!(
    castedData.teamId &&
    castedData.scenarioId &&
    typeof castedData.amount === 'number' &&
    castedData.shareToken
  );
};

export const RunFigmaConfidenceSimulationModalContent = () => {
  const { t } = useI18n();
  const { closeModal } = useModalContext();
  const { modalData, openedModalId } = useModalWrapperContext();
  const [persona, setPersona] = useState('default');
  const currentUser = useCurrentUser();

  const simulationData = useMemo(() => {
    const currentModalData = modalData[openedModalId];

    if (isRunFigmaConfidenceSimulationModalData(currentModalData)) {
      return currentModalData;
    }

    return null;
  }, [modalData, openedModalId]);

  const { canRunSimulations } = usePrototypePermissions(
    simulationData?.shareToken || ''
  );
  const { mutate: startExtendedMutation, isPending: isExtendedPending } =
    useStartAiExtendedReviewMutation();
  const { mutate: startSingleMutation, isPending: isSinglePending } =
    useStartAiReviewMutation(currentUser?.authorized ? 'authorized' : 'guest');

  const isPending = isExtendedPending || isSinglePending;

  const runSimulation = () => {
    if (!simulationData) return;

    const { scenarioId, amount } = simulationData;

    if (!canRunSimulations(amount)) {
      return;
    }

    if (amount === 1) {
      startSingleMutation({ scenarioId, persona });
    } else {
      startExtendedMutation({ scenarioId, persona, amount });
    }

    closeModal();
  };

  if (!simulationData?.teamId) return null;

  return (
    <Modal data-testid="run-figma-confidence-simulation-modal">
      <Modal.Header title="prototypes:more_confidence.select_persona_title" />
      <Modal.Content>
        <ModalContent>
          <Typography variant="body2">
            {t('prototypes:more_confidence.select_persona_description')}
          </Typography>
          <StyledPersonaSelector
            variant="outlined"
            value={persona}
            teamId={simulationData.teamId}
            onChange={setPersona}
            disabled={isPending}
          />
        </ModalContent>
      </Modal.Content>
      <Modal.Controls>
        <ButtonsContainer>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => closeModal()}
            disabled={isPending}
          >
            {t('shared:cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={runSimulation}
            disabled={isPending || !simulationData}
          >
            {t('prototypes:more_confidence.run_more_simulations', {
              count: simulationData?.amount || 1,
            })}
          </Button>
        </ButtonsContainer>
      </Modal.Controls>
    </Modal>
  );
};
